import axios from 'axios';
import { clearStoredAuth } from '../utils/auth';

const configuredApiUrl = process.env.REACT_APP_API_URL;
const apiBaseUrl =
  process.env.NODE_ENV === 'production' && configuredApiUrl === 'http://localhost:8080'
    ? ''
    : configuredApiUrl;

// Base API configuration
const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    console.log('🔐 [API Interceptor] Request:', config.url);
    console.log('  - Token exists:', !!token);
    console.log('  - Token (first 20 chars):', token ? token.substring(0, 20) + '...' : 'null');
    console.log('  - User:', user ? JSON.parse(user).username : 'null');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('  ✅ Set Authorization header');
    } else {
      console.log('  ⚠️ No token found, request will be unauthorized');
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    // Log response để debug user data
    if (response.config.url?.includes('/daily-pnl/') || response.config.url?.includes('/account/')) {
      console.log('📥 [API Response]:', response.config.url);
      console.log('  - Status:', response.status);

      // Log userId nếu có trong response
      if (response.data?.data?.userId || response.data?.data?.user_id) {
        const userId = response.data.data.userId || response.data.data.user_id;
        console.log('  - Response userId:', userId);

        // So sánh với user trong localStorage
        const localUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (localUser.id && localUser.id !== userId) {
          console.error('  ⚠️ WARNING: Response userId khác với localStorage userId!');
          console.error('    - localStorage userId:', localUser.id);
          console.error('    - Response userId:', userId);
        } else {
          console.log('  ✅ UserId match:', userId);
        }
      }

      // Log first item nếu là array
      if (Array.isArray(response.data?.data) && response.data.data.length > 0) {
        console.log('  - First item userId:', response.data.data[0].userId || response.data.data[0].user_id);
      }
    }

    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized
      console.log('❌ [API] 401 Unauthorized, redirecting to login...');
      clearStoredAuth();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API Endpoints
export const AUTH = {
  LOGIN: '/api/auth/login',
  LOGOUT: '/api/auth/logout',
  CHECK_AUTH: '/api/auth/check',
};

export const ACCOUNT = {
  BALANCES: '/api/account/balances',
  SYNC_BALANCES: '/api/account/sync-balances',
  FOUNDER_OVERVIEW: '/api/account/founder-overview',
};

export const TRADE = {
  HISTORY: '/api/auth/trade-history',
  RUNNING: '/api/history/running-trades',
  MANUAL_ENTRY: '/api/trade/manual',
  V18_INSPECT: '/api/trade/v18/inspect',
  V18_EVENTS: '/api/trade/v18/events',
  V18_STATS: '/api/trade/v18/stats',
  V18_ENTRY_FILTER_SETTINGS: '/api/trade/v18/entry-filter-settings',
  V18_XAG_TRADE_SETTINGS: '/api/trade/v18/xag-trade-settings',
  V18_SYMBOL_TRADE_SETTINGS: '/api/trade/v18/symbol-trade-settings',
  // Add more trade-related endpoints here
};

export const POSITION_HISTORY = {
  GET_BY_SYMBOL: (symbol) => `/api/position-history/${symbol}`,
  GET_ALL: '/api/position-history',
  SYNC: (symbol) => `/api/position-history/${symbol}/sync`,
  SYNC_ALL: '/api/position-history/sync',
  SUMMARY: '/api/position-history/summary',
};

export const DAILY_PNL = {
  SUMMARY: (startDate, endDate) =>
    `/api/daily-pnl/summary?startDate=${startDate}&endDate=${endDate}`,
  DETAILED: (startDate, endDate) =>
    `/api/daily-pnl/detailed?startDate=${startDate}&endDate=${endDate}`,
};

export const SYMBOL_CONTROL = {
  STATUS: (symbols) => `/api/symbol-control/status?symbols=${encodeURIComponent(symbols)}`,
  SKIP_ALL: '/api/symbol-control/skip-all',
};

export const ROE_GUARD = {
  STATUS: (symbols) => `/api/roe-guard/status?symbols=${encodeURIComponent(symbols)}`,
  EXCLUDE: '/api/roe-guard/exclude',
  BREAKEVEN: '/api/roe-guard/breakeven'
};

export const POSITION = {
  CLOSE: (symbol) => `/api/trade/position/${symbol}/close`,
  DCA_SAME_SIZE: (symbol) => `/api/trade/position/${symbol}/dca-same-size`,
};

export const OPEN_ORDERS = {
  LIST: '/api/open-orders',
  CANCEL: '/api/open-orders/cancel',
  REPLACE: '/api/open-orders/replace',
};

export const ENGINE = {
  STATUS: '/api/engine/status',
  SIGNALS: '/api/engine/signals',
  POSITIONS: '/api/engine/positions',
  RUNS: '/api/engine/runs',
};

export const SINGLE_SYMBOL = {
  PROFILE: '/api/single-symbol/profile',
  LEARNING_RUNS: '/api/single-symbol/learning-runs',
  LEARNING_RUN_DETAIL: (id) => `/api/single-symbol/learning-runs/${id}`,
  TRADE_QUALITY: '/api/single-symbol/trade-quality',
};

export const SETTINGS = {
  BASE_INVESTMENT: '/api/settings/base-investment',
  TEMPERATURE: '/api/settings/temperature',
  V17_INVESTMENT: '/api/settings/v17-investment',
  ROE_GUARD_THRESHOLD: '/api/settings/roe-guard-threshold',
};

export const HYPERLIQUID_MONITOR = {
  STATUS: '/api/hyperliquid-wallet-monitor/status',
  CONFIG: '/api/hyperliquid-wallet-monitor/config',
  SYNC: '/api/hyperliquid-wallet-monitor/sync',
  EVENTS: '/api/hyperliquid-wallet-monitor/events',
};

export const FAVORITES = {
  LIST: '/api/favorites',
  TOGGLE: '/api/favorites/toggle',
};

export const NEWS = {
  FOLLOWIN_SENTIMENT: '/api/news/followin/sentiment',
  FOLLOWIN_HEADLINES: '/api/news/followin/headlines',
};

export const CHECKINS = {
  LIST: '/api/checkins',
  SUMMARY: '/api/checkins/summary',
  CREATE: '/api/checkins',
  UPDATE: (id) => `/api/checkins/${id}`,
  ASSETS: '/api/checkins/assets',
  ASSET_UPDATE: (symbol) => `/api/checkins/assets/${symbol}`
};

export const DEGO_SCAN = {
  LATEST: '/api/dego-scan',
  RUN: '/api/dego-scan/scan',
};

export const ATR_FIBONACCI_SCAN = {
  LATEST: '/api/atr-fibonacci-scan',
  RUN: '/api/atr-fibonacci-scan/scan',
};

export const TYLER_SCAN = {
  LATEST: '/api/tyler-scan',
  RUN: '/api/tyler-scan/scan',
  SETTINGS: '/api/tyler-scan/settings',
};

export const RSI_SCAN = {
  LATEST: '/api/rsi-scan',
  RUN: '/api/rsi-scan/scan',
};

export const VP_SUPERTREND = {
  STATUS: '/api/vp-supertrend/status',
  CONFIG: '/api/vp-supertrend/config',
  CONFIGS: '/api/vp-supertrend/configs',
  SIGNALS: '/api/vp-supertrend/signals',
  TRADES: '/api/vp-supertrend/trades',
  RUN_NOW: '/api/vp-supertrend/run-now',
  BINANCE_SYMBOLS: '/api/symbols/binance-names',
};

export const KRONOS = {
  STATUS: '/api/kronos/status',
  LOAD_MODEL: '/api/kronos/load-model',
  FORECAST: '/api/kronos/forecast',
};

export const ADMIN = {
  USERS: '/api/admin/users',
  SYMBOLS: '/api/admin/symbols',
  ACTIONS: '/api/admin/actions',
  OPEN_MARKET: '/api/admin/actions/open-market',
  CLOSE_MARKET: '/api/admin/actions/close-market',
  BALANCE_PREVIEW: '/api/admin/balance/preview',
  BALANCE_EXECUTE: '/api/admin/balance/execute'
};

// Helper functions
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

export const removeAuthToken = () => {
  delete api.defaults.headers.common['Authorization'];
  localStorage.removeItem('token');
};

export default api;
